<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import UIKit, { type ITableColumn } from '@pocketprep/ui-kit'
import { stripeModule } from '@/store/stripe/module'
import { instructorModule } from '@/store/instructor/module'
import { type Stripe } from 'stripe'
import { monthsAbrev } from '@/utils'
import AddPaymentMethodSidePanel from '@/components/SidePanels/AddPaymentMethod.vue'
import PurchaseSeatsModal from '@/components/SeatsModal/PurchaseSeatsModal.vue'
import { toastModule } from '@/store/toast/module'

const {
    ModalContainer,
    TablePagination,
    Table,
    Button: PocketButton,
    EmptyState,
    Icon,
} = UIKit

type TMappedTransaction = {
    id: string
    date: string
    numSeats: string
    totalAmount: string
    receipt: string | null
}

type TMappedPaymentMethod = {
    id: string
    last4: string
    expiration: string
    hasExpired: boolean
}

const showDeleteCardModal = ref(false)
const showAddPaymentMethodSidePanel = ref(false)
const isLoading = ref(false)
const currentPage = ref(0)
const perPage = ref(10)
const stripeCustomerId = ref<string | undefined>('')
const onlineTransactionsColumns: ITableColumn[] = [
    {
        name: 'Date',
        isSortDisabled: false,
        propName: 'date',
        styles: {
            minWidth: '250px',
        },
    }, {
        name: '# of Seats',
        propName: 'numSeats',
        styles: {
            maxWidth: '67px',
            textAlign: 'center',
        },
    }, {
        name: 'Total Amount',
        propName: 'totalAmount',
        styles: {
            maxWidth: '95px',
        },
    }, {
        name: 'Receipt',
        propName: 'receipt',
        styles: {
            maxWidth: '95px',
        },
    },
]
const cardToDelete = ref<null | string>(null)

const showPurchaseSeatsModal = ref(false)

const paymentMethods = computed(() => {
    return stripeModule.getters.getStripePaymentMethods().reduce((acc, pm) => {
        // Skip payment methods with no card
        if (pm.card) {
            const expiration = `${pm.card.exp_month}/${pm.card.exp_year.toString().slice(2)}`
            const hasExpired = new Date(pm.card.exp_year || 0, (pm.card.exp_month || 0) + 1, 0)
                .getTime() < new Date().getTime()

            acc.push({
                id: pm.id,
                last4: pm.card.last4,
                expiration,
                hasExpired,
            })
        }
        return acc
    }, [] as TMappedPaymentMethod[])
})

const receipts = computed(() => {
    return stripeModule.getters.getReceipts()
})

const formatDate = (receiptEpoch: number) => {
    const date = new Date(receiptEpoch * 1000)
    return `${monthsAbrev[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
}

const transactions = computed(() => {
    return Object.values(receipts.value).reduce((acc, r) => {
        // Has a created date and successfully paid
        const isCompletedTransaction = r.created && r.status === 'succeeded'
        if (isCompletedTransaction) {
            // We expanded the Charge object in server
            const charge = r.latest_charge as Stripe.Charge
            const metadata = r.metadata
            const date = formatDate(r.created)
            acc.push({
                id: r.id,
                date,
                numSeats: metadata.totalSeats || '--',
                totalAmount: (r.amount_received / 100).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                }),
                receipt: charge.receipt_url,
            })

        }
        return acc
    }, [] as TMappedTransaction[])
})

const pagedTransactions = computed(() => {
    const perPageVal = perPage.value
    return transactions.value.slice(currentPage.value * perPageVal, (currentPage.value * perPageVal) + perPageVal)
})

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

onMounted(async () => {
    isLoading.value = true
    await instructorModule.actions.fetchCurrentOrgInstructor()
    stripeCustomerId.value = currentOrgInstructor.value?.stripeCustomerId

    if (stripeCustomerId.value) {
        // OrgInstructor has a record, so let's get their receipts
        await Promise.all([
            stripeModule.actions.fetchReceipts(),
            stripeModule.actions.fetchPaymentMethods(),
        ])
    }

    isLoading.value = false
})

const changePage = (newPage: number) => {
    currentPage.value = newPage
}

const viewReceipt = (receiptUrl: string) => {
    window.open(receiptUrl)
}

const deleteCard = async () => {
    isLoading.value = true
    showDeleteCardModal.value = false
    if (cardToDelete.value) {
        await stripeModule.actions.deletePaymentMethod(cardToDelete.value)
        toastModule.actions.displayToast({
            title: 'Card deleted.',
        })
    }
    isLoading.value = false
}

const showModal = (pmId: string) => {
    showDeleteCardModal.value = true
    cardToDelete.value = pmId
}

const removeModal = () => {
    showDeleteCardModal.value = false
    // Null out the pending card deletion
    cardToDelete.value = null
}
</script>

<template>
    <div class="purchases">
        <PurchaseSeatsModal
            v-if="showPurchaseSeatsModal"
            @close="showPurchaseSeatsModal = false"
        />
        <AddPaymentMethodSidePanel
            v-if="showAddPaymentMethodSidePanel"
            key="purchases"
            @close="showAddPaymentMethodSidePanel = false"
        />
        <Portal to="modal">
            <ModalContainer
                v-if="showDeleteCardModal"
                :show-close-button="true"
                :remove-head-border-bottom="true"
                @close="showDeleteCardModal = false">
                <template #head>
                    <div
                        class="purchases__delete-card-head"
                    >
                        Delete Card?
                    </div>
                </template>
                <div
                    class="purchases__delete-modal"
                >
                    <span
                        class="purchases__delete-text"
                    >
                        Your card will be deleted from our system. It won't be an option for future purchases.
                    </span>
                    <div
                        class="purchases__delete-buttons"
                    >
                        <PocketButton
                            class="purchases__nevermind-btn"
                            type="tertiary"
                            @click="removeModal"
                        >
                            Never Mind
                        </PocketButton>
                        <PocketButton
                            class="purchases__delete-btn"
                            @click="deleteCard"
                        >
                            Delete Card
                        </PocketButton>
                    </div>
                </div>
            </ModalContainer>
        </Portal>
        <div class="purchases__title">
            Purchases
        </div>
        <div class="purchases__online-transactions">
            <label>Online Transactions</label>
            <Table
                class="purchases__transactions-table"
                :columns="onlineTransactionsColumns"
                :rows="pagedTransactions"
                :table-row-styles="{
                    paddingRight: '0',
                    height: '45px',
                }"
                :table-column-labels-styles="{
                    paddingRight: '0',
                }"
                :table-column-label-styles="{
                    height: '20px',
                }"
                :table-cell-styles="{
                    height: '20px',
                }"
                :show-header="false"
            >
                <template #tableCellValue="{ row, column }">
                    <div
                        class="purchases__numSeats-cell"
                        v-if="column.propName === 'numSeats'"
                    >
                        {{ row.numSeats }}
                    </div>
                    <PocketButton
                        v-if="column.propName === 'receipt'"
                        type="tertiary"
                        @click="() => row.receipt && viewReceipt(row.receipt)"
                    >
                        View Receipt
                    </PocketButton>
                </template>
                <template #tableRows>
                    <Icon
                        v-if="isLoading"
                        type="loading2"
                        class="purchases__loading"
                    />
                    <div
                        v-if="!isLoading && !pagedTransactions.length"
                        class="purchases__empty-state-container"
                    >
                        <EmptyState
                            class="purchases__empty-state"
                            title="You have not purchased seats online"
                            message="Seats paid for through an emailed invoice won't show up here."
                        >
                            <template #imageRow>
                                <div />
                            </template>
                        </EmptyState>
                        <PocketButton
                            class="purchases__purchase-btn"
                            @click="showPurchaseSeatsModal = true"
                        >
                            Purchase Seats
                        </PocketButton>
                    </div>
                </template>
            </Table>
            <TablePagination
                v-if="pagedTransactions.length"
                class="purchases__pagination"
                unit="Transactions"
                :per-page="perPage"
                :current-page="currentPage"
                :total="transactions.length"
                @changePage="changePage"
            />
            <label>Wallet</label>
            <div v-if="!paymentMethods.length" class="purchases__no-cards">
                You have no saved credit/debit cards.
            </div>
            <div class="purchases__cards">
                <div
                    v-for="pm in paymentMethods"
                    :key="pm.id"
                    class="purchases__card"
                    :class="{ 'purchases__card--expired': pm.hasExpired }"
                >
                    <div class="purchases__card-title">
                        Card ending in {{ pm.last4 }}
                    </div>
                    <div class="purchases__card-expiration">
                        {{ pm.hasExpired ? 'Expired' : 'Expires' }}
                        {{ pm.expiration }}
                    </div>
                    <PocketButton
                        type="tertiary-small"
                        class="purchases__card-delete"
                        :disabled="isLoading"
                        @click="showModal(pm.id)"
                    >
                        Delete
                    </PocketButton>
                </div>
            </div>
            <PocketButton
                type="tertiary-small"
                @click="showAddPaymentMethodSidePanel = true"
            >
                + Add Card
            </PocketButton>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.purchases {
    label {
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 16px;
        display: block;
    }

    &__title {
        font-size: 26px;
        line-height: 36px;
        font-weight: 600;
        margin-left: 11px;
        margin-bottom: 32px;
    }

    &__transactions-table {
        max-width: 796px;
    }

    &__pagination {
        display: flex;
        justify-content: flex-end;
        margin: 16px 0 72px;
    }

    &__empty-state-container {
        text-align: center;
        margin-bottom: 72px;
    }

    &__loading {
        margin: 80px auto;
    }

    &__empty-state {
        margin: 0 auto;
        padding: 48px 0 22px;
    }

    &__no-cards {
        margin-bottom: 18px;
        font-size: 14px;
        line-height: 17px;
    }

    &__card {
        position: relative;
        width: 320px;
        height: 74px;
        border-radius: 6px;
        margin-bottom: 12px;
        padding: 16px 0 0 20px;
        box-sizing: border-box;
        border: 1px solid $fog;
        background: $white;

        &--expired {
            border: 1px solid rgba($brand-red, 0.5);
            background: $red-hint;
            box-shadow: none;

            &--dark {
                background: rgba($brand-red, 0.3);
                border-color: rgba($rosa, 0.5);
            }
        }
    }

    &__card-title {
        font-size: 15px;
        line-height: 18px;
        font-weight: 600;
        margin-bottom: 4px;
    }

    &__card-expiration {
        font-size: 13px;
        line-height: 18px;
        color: $slate-01;
    }

    &__card-delete {
        position: absolute;
        right: 24px;
        top: 17px;
    }

    &__delete-card-head {
        margin: 60px auto 25px;
        font-size: 18px;
        color: $brand-black;
        font-weight: 600;
    }

    &__delete-modal {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 374px;
        box-sizing: border-box;
    }

    &__nevermind-btn {
        margin-right: 18px;
        color: $slate;
    }

    &__delete-buttons {
        display: flex;
        margin: 32px 0 24px;
    }

    &__delete-text {
        font-size: 15px;
        color: $ash;
        width: 294px;
        text-align: center;
        margin: 20px 0 0;
    }

    &__numSeats-cell {
        width: 100%;
        text-align: right;
        padding-right: 15px;
    }
}
</style>
